/**
 * HTTP Client with JWT Token Management
 * @description 경로 기반 Tenant/Admin 토큰 선택, 401 시 영역별 로그인 페이지 리다이렉트
 */

import { navigateExternal, getCurrentPath } from '@shared/router';
import { config } from '@shared/config';
import { getAuthTokenForRequest } from '@/utils/authStorage';
import { getTenantAuthStorage, getAdminAuthStorage } from '@/utils/authStorage';

export interface HttpClientOptions extends RequestInit {
    skipAuth?: boolean;
}

const LOGIN_TENANT = '/login';
const LOGIN_ADMIN = '/admin/login';

/** 로그인·MFA 검증 등 인증 시도 API — 401은 세션 만료가 아님 */
function isAuthAttemptRequest(url: string): boolean {
    return (
        /\/auth\/login(?:\?|$)/.test(url)
        || /\/admin\/auth\/login(?:\?|$)/.test(url)
        || /\/auth\/mfa\/verify(?:\?|$)/.test(url)
    );
}

function mapAuthFailureMessage(url: string, serverMessage?: string): string {
    const msg = (serverMessage || '').trim();
    if (msg && !msg.toLowerCase().includes('unauthorized') && msg !== 'invalid credentials') {
        return msg;
    }
    if (/\/auth\/mfa\/verify(?:\?|$)/.test(url)) {
        return '인증 코드가 올바르지 않거나 만료되었습니다.';
    }
    return '인증에 실패했습니다. 아이디·비밀번호를 확인해 주세요.';
}

class HttpClient {
    private baseUrl: string;

    constructor(baseUrl: string = '') {
        this.baseUrl = baseUrl;
    }

    /**
     * HTTP 요청 실행
     */
    async request<T>(url: string, options: HttpClientOptions = {}): Promise<T> {
        const { skipAuth = false, ...fetchOptions } = options;

        // 요청 시작 시점 경로 고정 (클릭 후 경로가 바뀌어도 이 요청에는 /entry 기준 토큰 사용)
        const pathAtRequest = getCurrentPath();

        // 기본 헤더 설정
        const headers: HeadersInit = {
            ...fetchOptions.headers,
        };

        // body가 있고 Content-Type이 설정되지 않은 경우에만 application/json 설정
        if (fetchOptions.body && !headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }

        // 인증이 필요한 경우: 요청 시점 경로 기준 토큰 사용 (레이스 방지)
        if (!skipAuth) {
            const token = getAuthTokenForRequest(pathAtRequest);
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            // Tenant 범위 API(/tenants/:id/...) 요청 시 X-Tenant-Id 필수 (백엔드 tenantContext)
            const tenantUser = getTenantAuthStorage().getUser();
            if (tenantUser?.userType === 'tenant-user' && tenantUser.tenantId && /\/tenants\/[^/]+\//.test(url)) {
                (headers as Record<string, string>)['X-Tenant-Id'] = tenantUser.tenantId;
            }
        }

        const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;

        try {
            const response = await fetch(fullUrl, {
                ...fetchOptions,
                headers,
            });

            // 401 — 로그인 화면/API는 인증 실패, 그 외는 세션 만료
            if (response.status === 401) {
                const requestPath = url.startsWith('http') ? new URL(url).pathname : url;
                if (isAuthAttemptRequest(requestPath)) {
                    let serverMessage: string | undefined;
                    try {
                        const errorData = await response.json();
                        serverMessage =
                            errorData?.message
                            || errorData?.error?.message
                            || (typeof errorData?.error === 'string' ? errorData.error : undefined);
                    } catch {
                        /* 본문 없음 */
                    }
                    throw new Error(mapAuthFailureMessage(requestPath, serverMessage));
                }
                this.handleUnauthorized(pathAtRequest);
                throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
            }

            // 403 Forbidden - 권한 없음
            if (response.status === 403) {
                throw new Error('접근 권한이 없습니다.');
            }

            // 기타 HTTP 에러
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    errorData = { message: `HTTP Error: ${response.status} ${response.statusText}` };
                }
                
                const error = new Error(errorData.message || errorData.error?.message || `HTTP Error: ${response.status}`);
                (error as any).status = response.status;
                (error as any).response = errorData;
                throw error;
            }

            // JSON 응답 파싱
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return response.json();
            }

            // 텍스트 응답
            return response.text() as unknown as T;
        } catch (error) {
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('네트워크 오류가 발생했습니다.');
        }
    }

    /**
     * 401 Unauthorized 처리: 영역별 저장소 정리 후 해당 로그인 페이지로 리다이렉트
     * @param pathAtRequest 요청을 보낸 시점의 경로 (레이스 방지용, 없으면 현재 경로 사용)
     */
    private handleUnauthorized(pathAtRequest?: string): void {
        const path = pathAtRequest ?? getCurrentPath();
        const isAdminArea = path.startsWith('/admin');

        if (isAdminArea) {
            getAdminAuthStorage().clear();
            getTenantAuthStorage().clear();
        } else {
            getTenantAuthStorage().clear();
        }

        const loginPath = isAdminArea ? LOGIN_ADMIN : LOGIN_TENANT;
        if (path !== LOGIN_TENANT && path !== LOGIN_ADMIN) {
            sessionStorage.setItem('auth_redirecting', 'true');
            navigateExternal(loginPath);
        }
    }

    /**
     * GET 요청
     */
    async get<T>(url: string, options?: HttpClientOptions): Promise<T> {
        return this.request<T>(url, { ...options, method: 'GET' });
    }

    /**
     * POST 요청
     */
    async post<T>(url: string, data?: any, options?: HttpClientOptions): Promise<T> {
        return this.request<T>(url, {
            ...options,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * PUT 요청
     */
    async put<T>(url: string, data?: any, options?: HttpClientOptions): Promise<T> {
        return this.request<T>(url, {
            ...options,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * PATCH 요청
     */
    async patch<T>(url: string, data?: any, options?: HttpClientOptions): Promise<T> {
        return this.request<T>(url, {
            ...options,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * DELETE 요청
     */
    async delete<T>(url: string, options?: HttpClientOptions): Promise<T> {
        return this.request<T>(url, { ...options, method: 'DELETE' });
    }
}

// 기본 HTTP 클라이언트 인스턴스
export const httpClient = new HttpClient();

// Backend API용 HTTP 클라이언트 (Backend-API-이름정책: /api/portal prefix)
export const apiClient = new HttpClient(`${config.apiUrl}/api/portal`);